import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { IconBuilding, IconBrush, IconCheck } from "@tabler/icons-react";
import Swal from "sweetalert2";
import OrganizationService from "../services/OrganizationServices";

const EditOrganizationModal = ({ show, onHide, organizationData, onSuccess }) => {
  const [formData, setFormData] = useState({ name: "", brand_name: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Pré-preencher com os nomes atuais sempre que o modal abrir
  useEffect(() => {
    if (show && organizationData) {
      setFormData({
        name: organizationData.name || "",
        brand_name: organizationData.brand_name || "",
      });
      setErrors({});
    }
  }, [show, organizationData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validation = OrganizationService.validateOrganizationData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setSaving(true);
    setErrors({});

    try {
      const response = await OrganizationService.updateOrganization(
        organizationData.id,
        { name: formData.name.trim(), brand_name: formData.brand_name.trim() }
      );

      await Swal.fire({
        icon: "success",
        title: "Nomes atualizados!",
        text: "O nome da organização e da marca foram salvos com sucesso.",
        timer: 2000,
        showConfirmButton: false,
      });

      if (onSuccess) {
        onSuccess(response);
      }
    } catch (error) {
      console.error("Erro ao atualizar organização:", error);

      const serverErrors = error.response?.data;
      if (serverErrors && typeof serverErrors === "object") {
        setErrors(serverErrors);
        const firstError = Object.values(serverErrors)[0];
        Swal.fire({
          icon: "error",
          title: "Não foi possível salvar",
          text: Array.isArray(firstError) ? firstError[0] : String(firstError),
          confirmButtonColor: "#dc3545",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Erro inesperado",
          text: "Ocorreu um erro ao salvar os nomes. Tente novamente.",
          confirmButtonColor: "#dc3545",
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <IconBuilding size={20} className="me-2" />
          Editar Nomes da Organização
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold d-flex align-items-center">
              <IconBuilding size={16} className="me-2" />
              Nome da organização
            </Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Ex: Minha Agência Digital"
              maxLength={150}
              isInvalid={!!errors.name}
              autoFocus
            />
            <Form.Control.Feedback type="invalid">
              {errors.name}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Nome principal da sua organização.
            </Form.Text>
          </Form.Group>

          <Form.Group>
            <Form.Label className="fw-semibold d-flex align-items-center">
              <IconBrush size={16} className="me-2" />
              Nome da marca
            </Form.Label>
            <Form.Control
              type="text"
              name="brand_name"
              value={formData.brand_name}
              onChange={handleInputChange}
              placeholder="Ex: Agência Digital Pro"
              maxLength={100}
              isInvalid={!!errors.brand_name}
            />
            <Form.Control.Feedback type="invalid">
              {errors.brand_name}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Nome usado em emails, notificações e demais partes do sistema que
              representam sua marca.
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Salvando...
              </>
            ) : (
              <>
                <IconCheck size={16} className="me-1" />
                Salvar
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditOrganizationModal;
